import type { AgentDraft } from "@nemo/agent-schema";
import type { Source } from "../api.js";
import type { ChecklistId, WarningId } from "./text.js";

/** ok: fine; tip: something to improve; todo: not done yet; manual: only the owner can tell, by trying the preview. */
export type Status = "ok" | "tip" | "todo" | "manual";
export interface Item {
  id: ChecklistId;
  status: Status;
}

export const PERSONA_WARN_OVER = 3000;
const GENERIC_WORDS = new Set(["price", "precio", "info", "hola", "hello", "help", "ayuda"]);
const THIN_KNOWLEDGE_CHARS = 1500;
/** Advice only: more questions than this can make visitors leave. Not a limit anywhere. */
export const LEAD_FIELDS_TIP_OVER = 12;

/** One word under five letters, or one of the very common ones: it would answer for questions that need a real answer. */
export function isGenericTrigger(trigger: string): boolean {
  const w = plain(trigger.trim());
  return w !== "" && !/\s/.test(w) && (w.length < 5 || GENERIC_WORDS.has(w));
}

const plain = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** "do not", "never", "only", "nunca", "solo"...: the persona draws at least one line the agent will not cross. */
const LIMIT_PHRASE = /\b(do not|don't|never|only|no\s+das|nunca|jamas|no\s+hables|solo|unicamente)\b/;
const HAS_LENGTH = /\d+\s*(words?|palabras?|characters?|caracteres|sentences?|frases)|\b(brief|short|breve|corto)\b/;
const HAS_UNKNOWN = /(don'?t know|do not know|no sabe|no tengas|no tienes|offer|ofrece)/;

export interface Facts {
  form: AgentDraft;
  /** The knowledge sources as the server reports them (indexed or not), or null while unknown. */
  sources: Source[] | null;
}

const knowledgeOf = (f: AgentDraft) => f.knowledge ?? { faqs: [], topics: [], sources: [] };

function knowledgeChars(f: Facts): number {
  const k = knowledgeOf(f.form);
  let n = 0;
  for (const q of k.faqs ?? []) n += (q.question?.length ?? 0) + (q.answer?.length ?? 0);
  for (const t of k.topics ?? []) n += t.answer?.length ?? 0;
  for (const s of k.sources ?? []) if (s.type === "text") n += s.text.length;
  for (const s of f.sources ?? []) n += s.chunks * 800; // a stored piece is roughly this long
  return n;
}

export function computeChecklist(f: Facts): Item[] {
  const form = f.form;
  const persona = (form.persona ?? "").trim();
  const instr = plain(form.responseInstructions ?? "");
  const k = knowledgeOf(form);
  const indexed = (f.sources ?? []).some((s) => s.status === "indexed" && s.chunks > 0);
  const configured = (k.sources ?? []).length;
  const hasFaqOrTopic = (k.faqs ?? []).length > 0 || (k.topics ?? []).length > 0;
  const lead = form.leadCapture;

  const out: Item[] = [];
  out.push({ id: "persona", status: persona.length === 0 ? "todo" : persona.length > PERSONA_WARN_OVER || !LIMIT_PHRASE.test(plain(persona)) ? "tip" : "ok" });
  out.push({ id: "instructions", status: instr.trim() === "" ? "todo" : HAS_LENGTH.test(instr) && HAS_UNKNOWN.test(instr) ? "ok" : "tip" });
  out.push({ id: "knowledge", status: indexed || hasFaqOrTopic || (f.sources === null && configured > 0) ? "ok" : configured > 0 ? "tip" : "todo" });
  out.push({ id: "common_questions", status: "manual" });
  out.push({ id: "honest_unknown", status: "manual" });
  out.push({ id: "languages", status: "manual" });
  out.push({ id: "exact_wording", status: (k.topics ?? []).length > 0 ? "ok" : "tip" });
  out.push({ id: "sliders", status: (form.strictness ?? 50) >= 70 && (form.creativity ?? 20) <= 40 ? "ok" : "tip" });
  out.push({
    id: "lead_capture",
    status: !lead?.enabled ? "ok" : lead.flow?.trigger?.trim() && lead.flow?.confirmation?.trim() && (lead.fields ?? []).length <= LEAD_FIELDS_TIP_OVER ? "ok" : "tip",
  });
  const domains = form.deployment?.website?.allowedDomains ?? [];
  out.push({ id: "domains", status: domains.length === 0 ? "todo" : "ok" });
  return out;
}

/** Soft warnings: never blockers. */
export function computeWarnings(f: Facts): WarningId[] {
  const form = f.form;
  const k = knowledgeOf(form);
  const out: WarningId[] = [];
  const hasAny = (k.faqs ?? []).length > 0 || (k.topics ?? []).length > 0 || (k.sources ?? []).length > 0 || (f.sources ?? []).length > 0;
  if (!hasAny) out.push("no_knowledge");
  if ((form.strictness ?? 50) >= 70 && hasAny && knowledgeChars(f) < THIN_KNOWLEDGE_CHARS) out.push("strict_thin");
  if ((form.persona ?? "").length > PERSONA_WARN_OVER) out.push("persona_long");
  const generic = (k.topics ?? []).some((t) => (t.enabled !== false) && (t.triggers ?? []).some(isGenericTrigger));
  if (generic) out.push("topic_generic");
  return out;
}

export const autoItems = (items: Item[]) => items.filter((i) => i.status !== "manual");
